import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@se-platform/ui/utils';
import { GroupLabel } from './way-in-type';

// Fields on the brand are softer and larger than the design system's: 12px radius,
// 1px hairline, 18px input text. The design system's Input and Select carry the
// marketplace's 40px control height and Roboto sizing, so these thin wrappers own
// the brand geometry instead of fighting those classes.
const CONTROL_CLASSES =
  'w-full rounded-card border border-brand-line bg-brand-paper text-brand-ink transition-colors focus:border-brand-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2';

const CONTROL_SIZE_CLASSES = {
  default: 'px-[17px] py-[15px] font-display text-brand-input',
  small: 'px-3 py-[15px] font-display text-brand-input-sm',
} as const;

type FieldSize = keyof typeof CONTROL_SIZE_CLASSES;

interface FieldShellProps {
  label: string;
  // One line under the control, in Dark Gray.
  hint?: React.ReactNode;
  children: React.ReactNode;
}

function FieldShell({ label, hint, children }: FieldShellProps) {
  return (
    <label className="block">
      <span className="mb-2 block font-display text-brand-field-label uppercase text-brand-muted">
        {label}
      </span>
      {children}
      {hint ? <span className="mt-2 block text-brand-meta text-brand-muted">{hint}</span> : null}
    </label>
  );
}

interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  hint?: React.ReactNode;
  size?: FieldSize;
}

export function TextField({ label, hint, size = 'default', className, ...props }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint}>
      <input className={cn(CONTROL_CLASSES, CONTROL_SIZE_CLASSES[size], className)} {...props} />
    </FieldShell>
  );
}

interface SelectFieldProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  hint?: React.ReactNode;
  size?: FieldSize;
  options: ReadonlyArray<{ value: string; label: string }>;
}

export function SelectField({
  label,
  hint,
  size = 'default',
  options,
  className,
  ...props
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint}>
      <span className="relative block">
        <select
          className={cn(
            CONTROL_CLASSES,
            CONTROL_SIZE_CLASSES[size],
            'appearance-none pr-10',
            className,
          )}
          {...props}
        >
          {options.map((currentOption) => (
            <option key={currentOption.value} value={currentOption.value}>
              {currentOption.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted"
        />
      </span>
    </FieldShell>
  );
}

/**
 * A titled group of fields on the Background ground: "The legal entity",
 * "Headquarters". Also hosts the derived-terms panel on the services screen.
 */
export function FieldGroup({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-card bg-brand-ground px-7 py-[26px]', className)}>
      <GroupLabel className="mb-[18px]">{label}</GroupLabel>
      {children}
    </section>
  );
}

/**
 * What we already know, as a strip of hairline-divided cells rather than a
 * sidebar: the buyer, their contact, the deadline.
 */
export function FactsStrip({ facts }: { facts: ReadonlyArray<{ key: string; value: string }> }) {
  return (
    <dl className="mt-7 flex flex-col overflow-hidden rounded-card bg-brand-ground sm:flex-row">
      {facts.map((currentFact) => (
        <div
          key={currentFact.key}
          className="flex-grow border-b border-brand-line px-[18px] py-[15px] last:border-0 sm:border-b-0 sm:border-r"
        >
          <dt className="mb-1 font-display text-brand-fact-label uppercase text-brand-muted">
            {currentFact.key}
          </dt>
          <dd className="m-0 font-display text-brand-fact text-brand-black">{currentFact.value}</dd>
        </div>
      ))}
    </dl>
  );
}
